import {
	type ActionFunctionArgs,
	type LoaderFunctionArgs,
	json,
	redirect,
} from "@remix-run/node";
import {
	type MetaFunction,
	isRouteErrorResponse,
	useLoaderData,
	useRouteError,
} from "@remix-run/react";
import { useEffect } from "react";
import PortalLanding from "~/components/portal-landing";

interface BranchData {
	tenant_id: string;
	name: string;
	type: string;
	address: string;
	city: string;
	province: string;
	phone: string;
	services: string[];
	operating_hours: string;
	operational_status: string;
	status_message?: string;
	coordinates: {
		lat: number;
		lng: number;
	};
	rating?: number;
	total_reviews?: number;
	image_url: string;
	booking_url: string;
}
import SignInBox from "~/routes/_index/sign-in-box";
import { serverCookie } from "~/utils/cookies.server";
import { useAppState } from "~/utils/states";
import {
	fetchBanner,
	fetchFaskesMeta,
	generateCsrfToken,
	getTenantId,
} from "~/utils/utils.server";
import { LocaleSelector } from "./lingui";

// biome-ignore lint/suspicious/noExplicitAny: default value
export const meta: MetaFunction = ({ data }: { data: any }) => {
	if (!data) return;
	return [
		{
			title: `Booking Online ${data.meta.name}`,
		},
	];
};

export const loader = async ({ request, params }: LoaderFunctionArgs) => {
	const url = new URL(request.url);
	const selectedTenant = url.searchParams.get("tenant");
	const selectedTenantDev = url.searchParams.get("selected_tenant");

	// Jika ada parameter tenant, redirect ke tenant yang dipilih
	if (selectedTenant) {
		const { hostname, protocol, port } = new URL(request.url);

		// Development mode - localhost atau .local domain
		if (
			hostname === "localhost" ||
			hostname === "127.0.0.1" ||
			hostname.startsWith("192.168")
		) {
			// Untuk localhost, gunakan query parameter
			const targetUrl = `${protocol}//${hostname}${port ? `:${port}` : ""}/?selected_tenant=${selectedTenant}`;
			return redirect(targetUrl);
		}
		if (hostname.endsWith(".local")) {
			// Untuk .local domain, gunakan subdomain seperti production
			const baseDomain = hostname.split(".").slice(1).join(".");
			const targetUrl = `${protocol}//c${selectedTenant}.${baseDomain}`;
			return redirect(targetUrl);
		}
		// Production mode - subdomain
		const baseDomain = hostname.split(".").slice(1).join(".");
		const targetUrl = `https://c${selectedTenant}.${baseDomain}`;
		return redirect(targetUrl);
	}

	// Untuk development mode, jika ada selected_tenant, override tenant-id untuk simulasi
	let customRequest = request;
	if (selectedTenantDev) {
		const headers = new Headers(request.headers);
		headers.set("x-tenant-id", selectedTenantDev);
		customRequest = new Request(request, { headers });
	}

	const hostname = new URL(request.url).hostname;
	// Check for direct tenant URLs:
	// Production: c1593663375705.trustbook.id
	// Testing: testing.booking-dev.his-next.id
	let isDirectTenantHostname =
		hostname.match(/^c\d+\./) || // Production tenant pattern
		hostname.includes("testing.booking-dev.his-next.id") || // Testing environment
		hostname.includes("testing.booking-dev.his-next.local") || // Local testing mirror
		hostname.includes("testing.trustbook.local"); // Local testing alternative

	// Check if using static TENANT_ID from environment (for custom domains like booking.dym.co.id)
	const { tenantId } = await getTenantId(customRequest);
	const isStaticTenant = !!process.env.TENANT_ID;
	isDirectTenantHostname =
		isStaticTenant === true ? true : isDirectTenantHostname;

	// Fetch faskesMeta for both portal and direct tenant hostnames
	// Portal hostnames use it for portal config, direct tenant hostnames use it for faskes info
	let faskesMeta = null;
	faskesMeta = await fetchFaskesMeta(customRequest);

	// Don't redirect if:
	// 1. It's a direct tenant hostname (c1234.trustbook.id, testing.booking-dev.his-next.id)
	// 2. Using static TENANT_ID (custom domain like booking.dym.co.id)
	// 3. Has valid faskesMeta
	if (!faskesMeta && !isDirectTenantHostname && !isStaticTenant) {
		// return redirect("https://trustmedis.com");
	}

	const showPortal =
		faskesMeta?.portal_enabled === true &&
		!isDirectTenantHostname &&
		!isStaticTenant;

	// Show normal booking interface if:
	// 1. Direct tenant hostname OR
	// 2. Static tenant (custom domain) OR
	// 3. Portal is disabled
	// Otherwise redirect to trustmedis.com
	if (!showPortal && !isDirectTenantHostname && !isStaticTenant) {
		// return redirect("https://trustmedis.com");
	}

	const banner = await fetchBanner(customRequest);
	const csrf = generateCsrfToken();

	return json({
		meta: faskesMeta,
		banner,
		csrf,
		showPortal,
		selectedTenant: selectedTenantDev,
	});
};

export const action = async ({ request }: ActionFunctionArgs) => {
	const body = await request.formData();
	const next = body.get("next")?.toString() || "/app";

	return redirect(next, {
		headers: {
			"Set-Cookie": await serverCookie.serialize({
				patient: body.get("patient"),
				expiredAt: serverCookie.expires,
			}),
		},
	});
};

export default function BookingHome() {
	const { meta, csrf, banner, showPortal, selectedTenant } =
		useLoaderData<typeof loader>();
	const { setFaskes, setBanner, setCsrf } = useAppState();

	useEffect(() => {
		if (meta) setFaskes(meta);
		setCsrf(csrf);
		setBanner(banner);
	}, [meta, csrf, banner, setFaskes, setCsrf, setBanner]);

	const handleSelectBranch = (branch: BranchData) => {
		// Check if branch is available
		if (branch.operational_status !== "open") {
			alert(
				`Cabang ${branch.name} sedang tidak tersedia: ${branch.operational_status}`,
			);
			return;
		}

		// Use booking_url from database if available, otherwise construct URL
		const targetUrl =
			branch.booking_url || constructBookingUrl(branch.tenant_id);

		// Redirect to target URL
		window.location.href = targetUrl;
	};

	const constructBookingUrl = (tenantId: string): string => {
		const { hostname, protocol } = new URL(window.location.href);
		const subdomain = tenantId.split(".")[0];

		if (hostname.endsWith(".local") || hostname === "localhost") {
			// Development mode
			if (hostname.endsWith(".trustbook.local")) {
				const baseDomain = hostname.split(".").slice(1).join(".");
				return `${protocol}//${subdomain}.${baseDomain}`;
			}
			// localhost fallback
			const url = new URL(window.location.href);
			url.searchParams.set("tenant", tenantId);
			return url.toString();
		}

		// Testing environment
		if (hostname.includes("portal-tm.booking-dev.his-next.id")) {
			return "https://testing.booking-dev.his-next.id/";
		}

		// Production - construct URL with portal domain
		const portalDomain = hostname.split(".").slice(1).join(".");
		return `https://${subdomain}.${portalDomain}`;
	};

	// Jika sudah ada selected_tenant dan bukan portal mode, tampilkan landing page faskes
	if (selectedTenant && !showPortal) {
		return (
			<div className="flex flex-row h-full">
				{/* LEFT SECTION */}
				<div
					className="w-full md:w-2/3 lg:w-1/2 flex items-center justify-center h-full"
					style={{
						backgroundImage: "url(/login-background.webp)",
						backgroundSize: "cover",
					}}
				>
					<div className="absolute left-4 top-4">
						<LocaleSelector />
					</div>
					<div className="absolute right-4 top-4">
						<a
							href="/"
							className="bg-white px-4 py-2 rounded shadow text-sm hover:bg-gray-100"
						>
							← Kembali ke Portal
						</a>
					</div>
					<SignInBox />
				</div>

				{/* RIGHT SECTION */}
				<div className="hidden md:block md:w-1/3 lg:w-1/2">
					<div
						className="h-full bg-cover bg-center"
						style={{
							backgroundImage: "url(/default-portal-banner.webp)",
						}}
					/>
				</div>
			</div>
		);
	}

	// Jika portal mode aktif, tampilkan portal landing page
	if (showPortal) {
		return (
			<div className="min-h-screen bg-gray-50">
				<PortalLanding
					onSelectBranch={handleSelectBranch}
					tenantId={meta?.tenantId || ""}
				/>
			</div>
		);
	}

	// Mode normal (faskes langsung)
	return (
		<div className="flex flex-row h-full">
			{/* LEFT SECTION */}
			<div
				className="w-full md:w-2/3 lg:w-1/2 flex items-center justify-center h-full"
				style={{
					backgroundImage: "url(/login-background.webp)",
					backgroundSize: "cover",
				}}
			>
				<div className="absolute left-4 top-4">
					<LocaleSelector />
				</div>
				<SignInBox />
			</div>

			{/* RIGHT SECTION */}
			<div className="hidden md:block md:w-1/3 lg:w-1/2">
				<div
					className="h-full bg-cover bg-center"
					style={{
						backgroundImage: "url(/login-banner-default.webp)",
					}}
				/>
			</div>
		</div>
	);
}

export function ErrorBoundary() {
	const error = useRouteError() as Error;

	let response = <pre className="text-red-500">{error.message}</pre>;
	if (isRouteErrorResponse(error)) {
		response = (
			<div>
				<pre className="text-red-500">
					{error.data} [{error.status}]
				</pre>
			</div>
		);
	}

	return (
		<div className="h-full w-full flex flex-col items-center justify-center">
			<p>Application Error!</p>
			<div className="mt-4">{response}</div>
		</div>
	);
}
